<template>
  <div
    v-if="eponClodeOnuData && Object.keys(eponClodeOnuData).length > 0"
  >
    <div class="p-4 chart_row_one_card">
      <div class="d-flex align-items-center mb-3">
        <p
          class="text-center p-0 m-0"
          style="font-size: 25px; line-height: 80px"
        >
          EPON CLOUDE ONU Status - {{ eponClodeOnuData.onu_name }}
          <span
            v-if="eponClodeOnuData?.running_state === 1"
            class="inline-block bg-success rounded-circle"
            style="height: 1rem; width: 1rem; display: inline-block; margin-right: 0.25rem;"
          ></span>
          <span
            v-else
            class="inline-block bg-danger rounded-circle"
            style="height: 1rem; width: 1rem; display: inline-block; margin-right: 0.25rem;"
          ></span>
          <sup class="text-xs">{{ port_number }}</sup>
        </p>
      </div>

      <div 
        class="d-flex flex-wrap w-100"
        v-if="!loading && eponClodeOnuData && Object.keys(eponClodeOnuData).length > 0"
      >
        <!-- All items with same fixed width -->
        <div
          class="d-flex align-items-center m-2 p-2 info-item"
          v-for="item in infoItems"
          :key="item.key"
        >
          <div
            class="rounded d-flex align-items-center justify-content-center icon-container"
          >
            <i :class="item.icon" class="text-white"></i>
          </div>
          <div class="p-2 content">
            <p class="m-0 label">{{ item.label }}</p>
            <p
              class="m-0 value"
            >
              {{ item.value }}
            </p>
          </div>
        </div>
      </div>

      <div class="row" v-else-if="loading">
        <Skalaton />
      </div>
    </div>
  </div>
</template>

<script>
import Skalaton from '@/components/Skalaton.vue';

export default {
  name: 'EponClodeOnuStatus',
  components: {
    Skalaton
  },
  props: {
    eponClodeOnuData: {
      type: Object,
      default: null
    },
    port_number: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    infoItems() {
      if (!this.eponClodeOnuData) return [];
      
      return [
        {
          key: 'onu_name',
          label: 'ONU Name',
          value: this.eponClodeOnuData.onu_name || 'N/A',
          icon: 'fas fa-tag'
        },
        {
          key: 'equip_id',
          label: 'Equipment ID',
          value: this.eponClodeOnuData.equip_id || 'N/A',
          icon: 'fas fa-microchip'
        },
        {
          key: 'onu_type',
          label: 'ONU Type',
          value: this.eponClodeOnuData.onu_type || 'N/A',
          icon: 'fas fa-network-wired'
        },
        {
          key: 'vendor_id',
          label: 'Vendor',
          value: this.eponClodeOnuData.vendor_id || 'N/A',
          icon: 'fas fa-industry'
        },
        {
          key: 'running_state',
          label: 'Running Status',
          value: this.getRunningStatusText(this.eponClodeOnuData.running_state),
          icon: 'fas fa-signal'
        },
        {
          key: 'config_state',
          label: 'Config State',
          value: this.getConfigStateText(this.eponClodeOnuData.config_state),
          icon: 'fas fa-cog'
        },
        {
          key: 'match_state',
          label: 'Match State',
          value: this.getMatchStateText(this.eponClodeOnuData.match_state),
          icon: 'fas fa-link'
        },
        {
          key: 'last_down_cause',
          label: 'Last Down Cause',
          value: this.getLastDownCauseText(this.eponClodeOnuData.last_down_cause),
          icon: 'fas fa-exclamation-triangle'
        },
        {
          key: 'rx_power',
          label: 'RX Power',
          value: `${this.eponClodeOnuData.rx_power} dBm`,
          icon: 'fas fa-broadcast-tower'
        },
        {
          key: 'tx_power',
          label: 'TX Power',
          value: `${this.eponClodeOnuData.tx_power} dBm`,
          icon: 'fas fa-satellite-dish'
        },
        {
          key: 'bias_current',
          label: 'Bias Current',
          value: `${this.eponClodeOnuData.bias_current} mA`,
          icon: 'fas fa-bolt'
        },
        {
          key: 'temperature',
          label: 'Temperature',
          value: `${this.eponClodeOnuData.temperature}°C`,
          icon: 'fas fa-thermometer-half'
        },
        {
          key: 'voltage',
          label: 'Voltage',
          value: `${this.eponClodeOnuData.voltage} V`,
          icon: 'fas fa-car-battery'
        },
        {
          key: 'hardware',
          label: 'Hardware Version',
          value: this.eponClodeOnuData.hardware || 'N/A',
          icon: 'fas fa-microchip'
        },
        {
          key: 'software',
          label: 'Software Version',
          value: this.eponClodeOnuData.software || 'N/A',
          icon: 'fas fa-code'
        },
        {
          key: 'mac',
          label: 'MAC Address',
          value: this.eponClodeOnuData.mac || 'N/A',
          icon: 'fas fa-network-wired'
        },
        {
          key: 'running_time',
          label: 'Uptime',
          value: this.formatUptime(this.eponClodeOnuData.running_time),
          icon: 'fas fa-clock'
        },
        {
          key: 'pon_id',
          label: 'PON ID',
          value: this.eponClodeOnuData.pon_id || 'N/A',
          icon: 'fas fa-plug'
        },
        {
          key: 'onu_id',
          label: 'ONU ID',
          value: this.eponClodeOnuData.onu_id || 'N/A',
          icon: 'fas fa-hashtag'
        },
        {
          key: 'eth_num',
          label: 'Ethernet Ports',
          value: this.eponClodeOnuData.eth_num || '0',
          icon: 'fas fa-ethernet'
        },
        {
          key: 'catv_num',
          label: 'CATV Ports',
          value: this.eponClodeOnuData.catv_num || '0',
          icon: 'fas fa-tv'
        },
        {
          key: 'pots_num',
          label: 'POTS Ports',
          value: this.eponClodeOnuData.pots_num || '0',
          icon: 'fas fa-phone'
        }
      ];
    }
  },
  methods: {
    getRunningStatusText(status) {
      const statusMap = {
        0: 'Offline',
        1: 'Online',
        2: 'Initializing',
        3: 'Failed'
      };
      return statusMap[status] || 'Unknown';
    },
    
    getConfigStateText(state) {
      const stateMap = {
        1: 'Initial',
        2: 'Checking',
        3: 'Configuring',
        4: 'Configured',
        5: 'Failed'
      };
      return stateMap[state] || 'Unknown';
    },
    
    getMatchStateText(state) {
      const stateMap = {
        0: 'Unmatched',
        1: 'Matched',
        2: 'Matching'
      };
      return stateMap[state] || 'Unknown';
    },
    
    getLastDownCauseText(cause) {
      const causeMap = {
        1: 'Power Fault',
        2: 'LOS (Loss of Signal)',
        3: 'LOF (Loss of Frame)',
        4: 'LOM (Loss of Management)',
        5: 'Manual Shutdown'
      };
      return causeMap[cause] || 'Unknown';
    },
    
    formatUptime(seconds) {
      if (!seconds) return 'N/A';
      
      const days = Math.floor(seconds / (24 * 60 * 60));
      const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
      const minutes = Math.floor((seconds % (60 * 60)) / 60);
      
      if (days > 0) {
        return `${days}d ${hours}h ${minutes}m`;
      } else if (hours > 0) {
        return `${hours}h ${minutes}m`;
      } else {
        return `${minutes}m`;
      }
    }
  }
}
</script>

<style scoped>
.chart_row_one_card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.info-item {
  width: 360px; /* Fixed width for all items */
  background-color: #0c1c35;
  border-radius: 8px;
  min-height: 80px;
  flex: 0 0 auto; /* Don't grow or shrink */
}

.icon-container {
  height: 50px;
  width: 50px;
  background-color: dimgrey;
  flex-shrink: 0; /* Prevent icon from shrinking */
}

.content {
  flex: 1;
  min-width: 0; /* Allow text to wrap */
}

.label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #ccc;
  margin-bottom: 4px;
}

.value {
  font-size: 0.875rem;
  color: greenyellow !important;
  word-break: break-word;
}

.bg-success {
  background-color: #28a745 !important;
}

.bg-danger {
  background-color: #dc3545 !important;
}

.text-xs {
  font-size: 0.75rem;
}

/* Ensure consistent spacing and wrapping */
.d-flex.flex-wrap {
  gap: 12px; /* Consistent gap between items */
}
</style>